/**
 * The command palette.
 *
 * DESIGN_LANGUAGE.md §1 ("Keyboard-first"): *"Every primary workflow is
 * completable without a pointing device. A command palette reaches every
 * navigable resource and every action the user is permitted to take."* This is
 * the foundation of that: a registry any part of the application contributes
 * to, and one dialog that searches it.
 *
 * # Why the ARIA is hand-written
 *
 * Radix has no command primitive, and the obvious dependency (`cmdk`) brings
 * its own opinions about rendering. The pattern itself is well specified — APG
 * "combobox with listbox popup" — and the four things it requires are the four
 * things below: `role="combobox"` with `aria-expanded` and `aria-controls` on
 * the input, `role="listbox"` on the list, `role="option"` with `aria-selected`
 * on each row, and `aria-activedescendant` moving the *virtual* focus while the
 * real focus stays in the input so that typing keeps working.
 *
 * The part people get wrong is the last one: moving real focus to the list on
 * arrow-down, which breaks typing and is why so many palettes cannot be
 * refined after the first keystroke.
 */

import { Dialog as RadixDialog } from 'radix-ui';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cx } from '~/lib/cx';
import { Kbd, modifierKey } from './Navigation';
import { Search } from './icons';

import styles from './CommandPalette.module.css';

export interface Command {
  id: string;
  label: string;
  /** The group heading this appears under. */
  group: string;
  /** Extra words that should match this command but are not shown. */
  keywords?: string[];
  icon?: ReactNode;
  /** A shortcut hint shown at the trailing edge. Display only. */
  shortcut?: string;
  run: () => void;
}

interface PaletteApi {
  open: () => void;
  close: () => void;
  /** Register commands for as long as the caller is mounted. Returns the undo. */
  register: (commands: Command[]) => () => void;
}

const PaletteContext = createContext<PaletteApi | null>(null);

export function useCommandPalette(): PaletteApi {
  const context = useContext(PaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>.');
  }
  return context;
}

/**
 * Contribute commands from a component, for as long as it is mounted.
 *
 * The dependency on `commands` is the caller's responsibility: pass a
 * `useMemo`'d array, or the registration churns on every render.
 */
export function useRegisterCommands(commands: Command[]): void {
  const { register } = useCommandPalette();
  useEffect(() => register(commands), [register, commands]);
}

/**
 * Match a query against a command.
 *
 * Subsequence matching on the lower-cased haystack — "ovw" finds "Overview" —
 * because that is what makes a palette faster than a menu. Scored so that an
 * earlier, tighter run of matches ranks above a scattered one, which is the
 * difference between typing two characters and typing five.
 */
export function scoreCommand(command: Command, query: string): number {
  if (!query) return 0;
  const needle = query.toLowerCase();
  const haystacks = [command.label, ...(command.keywords ?? []), command.group];

  let best = -1;
  for (const haystack of haystacks) {
    const text = haystack.toLowerCase();

    const exact = text.indexOf(needle);
    if (exact === 0) {
      best = Math.max(best, 1000);
      continue;
    }
    if (exact > 0) {
      best = Math.max(best, 800 - exact);
      continue;
    }

    // Subsequence.
    let index = 0;
    let score = 400;
    let previous = -1;
    for (const character of needle) {
      const found = text.indexOf(character, index);
      if (found === -1) {
        score = -1;
        break;
      }
      // A character adjacent to the last match is worth more than one ten
      // letters later: it means the user is typing a prefix, not a crossword.
      if (previous !== -1) score -= Math.min(found - previous - 1, 20);
      previous = found;
      index = found + 1;
    }
    best = Math.max(best, score);
  }
  return best;
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [registries, setRegistries] = useState<Command[][]>([]);
  /**
   * Where the keyboard was when the palette opened.
   *
   * Radix restores focus to the dialog's trigger on close, and this dialog has
   * no trigger: it is opened by a global ⌘K handler and by `open()` from the
   * top bar, neither of which is a `Dialog.Trigger`. So Radix had nothing to
   * restore to and focus landed on `<body>` — measured in a browser: focus on
   * the "Security" nav link, ⌘K, Escape, and `document.activeElement` is
   * `body`. For a keyboard user that is the whole page lost; the next Tab
   * starts again at the skip link. Remembering it here is the only place that
   * knows, because it is the only place that sees the transition.
   */
  const returnFocusTo = useRef<HTMLElement | null>(null);

  const setOpen = useCallback((next: boolean | ((current: boolean) => boolean)) => {
    setIsOpen((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      if (value && !current) {
        const active = document.activeElement;
        returnFocusTo.current = active instanceof HTMLElement ? active : null;
      }
      return value;
    });
  }, []);

  /**
   * Put it back, if it is still there.
   *
   * `isConnected` because a command may have navigated: the element that had
   * focus belonged to the previous page and is gone, and focusing a detached
   * node throws nothing but achieves nothing either. Letting focus fall to
   * `body` is the right outcome *after a navigation* — the new route owns it.
   */
  const restoreFocus = useCallback(() => {
    const element = returnFocusTo.current;
    returnFocusTo.current = null;
    if (element?.isConnected) element.focus();
  }, []);

  const open = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const register = useCallback((commands: Command[]) => {
    setRegistries((current) => [...current, commands]);
    return () => {
      setRegistries((current) => current.filter((entry) => entry !== commands));
    };
  }, []);

  const api = useMemo<PaletteApi>(() => ({ open, close, register }), [open, close, register]);
  const commands = useMemo(() => registries.flat(), [registries]);

  // ⌘K / Ctrl-K, globally. `metaKey || ctrlKey` rather than a platform check:
  // a user on a Mac with an external PC keyboard reaches for whichever one
  // their muscle memory has, and both should work.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [setOpen]);

  return (
    <PaletteContext.Provider value={api}>
      {children}
      {/* Mounted only while open, so its query and selection start empty every
       * time without an effect resetting them. Resetting derived state in an
       * effect renders once with the stale value and once more after — which
       * on a palette is a visible flash of the previous search. */}
      {isOpen ? (
        <CommandPalette open onOpenChange={setOpen} onClosed={restoreFocus} commands={commands} />
      ) : null}
    </PaletteContext.Provider>
  );
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once the dialog has gone, so the caller can put focus back. */
  onClosed?: () => void;
  commands: Command[];
}

export function CommandPalette({ open, onOpenChange, onClosed, commands }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    if (!query.trim()) return commands;
    return commands
      .map((command) => ({ command, score: scoreCommand(command, query.trim()) }))
      .filter((entry) => entry.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.command);
  }, [commands, query]);

  const activeId = matches[activeIndex] ? `${listId}-${matches[activeIndex].id}` : undefined;

  // Keep the virtually-focused row in view. `block: 'nearest'` so a row already
  // visible does not scroll the list under the pointer.
  useEffect(() => {
    if (!activeId) return;
    listRef.current
      ?.querySelector(`#${CSS.escape(activeId)}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  function runActive() {
    const command = matches[activeIndex];
    if (!command) return;
    onOpenChange(false);
    command.run();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (matches.length === 0 ? 0 : (current + 1) % matches.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) =>
        matches.length === 0 ? 0 : (current - 1 + matches.length) % matches.length,
      );
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(Math.max(matches.length - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      runActive();
    }
  }

  // Grouped for display, but the index is over the flat `matches` array, so
  // arrow keys cross group boundaries without the user noticing they exist.
  const groups = useMemo(() => {
    const out = new Map<string, { command: Command; index: number }[]>();
    matches.forEach((command, index) => {
      const bucket = out.get(command.group) ?? [];
      bucket.push({ command, index });
      out.set(command.group, bucket);
    });
    return [...out.entries()];
  }, [matches]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.backdrop} />
        <RadixDialog.Content
          className={styles.palette}
          // Named by `Dialog.Title` below. An `aria-label` here as well would
          // be dead weight — `aria-labelledby` wins over `aria-label`, so the
          // second name is never read and silently rots when one is edited.
          onCloseAutoFocus={(event) => {
            // Radix would focus the trigger. There is no trigger, so its
            // fallback is `body`. Take it over and restore what we recorded.
            event.preventDefault();
            onClosed?.();
          }}
        >
          <RadixDialog.Title className={styles.visuallyHidden}>Command palette</RadixDialog.Title>
          <RadixDialog.Description className={styles.visuallyHidden}>
            Search for a page or an action. Use the arrow keys to move and Enter to run.
          </RadixDialog.Description>

          <div className={styles.inputRow}>
            <Search className={styles.inputIcon} />
            <input
              className={styles.input}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                // Here rather than in an effect on `query`: this is the event
                // that invalidated the selection, so this is where it is
                // reset. An effect would render the new list with the old
                // index selected for one frame.
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search pages and actions"
              aria-label="Search pages and actions"
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              {...(activeId ? { 'aria-activedescendant': activeId } : {})}
              autoComplete="off"
              spellCheck={false}
            />
            <Kbd>Esc</Kbd>
          </div>

          <div className={styles.list} id={listId} role="listbox" ref={listRef}>
            {matches.length === 0 ? (
              <p className={styles.noResults}>No matching page or action.</p>
            ) : (
              groups.map(([group, entries]) => (
                <div key={group} className={styles.group}>
                  <p className={styles.groupLabel} aria-hidden="true">
                    {group}
                  </p>
                  {entries.map(({ command, index }) => (
                    <button
                      key={command.id}
                      id={`${listId}-${command.id}`}
                      type="button"
                      role="option"
                      // Out of the tab order. APG's combobox-with-listbox keeps
                      // real focus in the input and moves a virtual cursor with
                      // `aria-activedescendant`; an option the user could tab to
                      // would break typing after the first arrow key.
                      tabIndex={-1}
                      aria-selected={index === activeIndex}
                      className={cx(styles.item, index === activeIndex && styles.itemActive)}
                      onMouseDown={(event) => {
                        // Keep focus in the input. Without this the browser
                        // focuses the button on mousedown, the combobox loses
                        // focus, and the palette stops responding to the
                        // keyboard mid-interaction.
                        event.preventDefault();
                      }}
                      onMouseMove={() => {
                        // `mousemove` and not `mouseenter`: with the keyboard
                        // driving, the list scrolls under a stationary pointer
                        // and `mouseenter` fires on a row the user never
                        // pointed at, yanking the selection back.
                        setActiveIndex(index);
                      }}
                      onClick={() => {
                        onOpenChange(false);
                        command.run();
                      }}
                    >
                      <span className={styles.itemIcon}>{command.icon}</span>
                      <span className={styles.itemLabel}>{command.label}</span>
                      {command.shortcut ? <Kbd>{command.shortcut}</Kbd> : null}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** The hint shown in the top bar. Exported so the shortcut is written once. */
export function commandPaletteHint(): string {
  return `${modifierKey()}K`;
}
